import { useMutation } from '@tanstack/react-query';
import { downloadCallLogs } from '@/api/logs';
import moment from 'moment';
import { DownloadType } from 'src/enums/ELogs';

interface DownloadParams {
	startDate?: any;
	endDate?: any;
	isNoviUser: boolean;
	agentId: string;
	textLogs?: any[];
	voicemailLogs?: any[];
	timeZone?: string;
}

const renderPSTDate = (date: any, timeZone: string) => {
	return moment(date).tz(timeZone).format('YYYY-MM-DD HH:mm:ss');
};

export const useDownloadHandler = () => {
	const { mutate: downloadCallLogsMutation, status: downloadCallLogsStatus } = useMutation({
		mutationKey: ['downloadCallLogs'],
		mutationFn: downloadCallLogs,
	});

	const handleTextLogsDownload = (
		textLogs: any[],
		timeZone: string,
		dateRange?: [any, any] | null,
	) => {
		let filteredLogs = textLogs;

		if (dateRange?.[0] && dateRange?.[1]) {
			const startDate = dateRange[0].valueOf();
			const endDate = dateRange[1].valueOf();
			filteredLogs = textLogs.filter((log) => {
				const logDate = new Date(log.date).valueOf();
				return logDate >= startDate && logDate <= endDate;
			});
		}

		const headingRow = 'Date,To,Text,Text Type\n';
		const csv = filteredLogs
			.map((log) => {
				return `${renderPSTDate(log.date, timeZone)},${log.to},${log.text},${log.textType}`;
			})
			.join('\n');
		const csvData = headingRow + csv;
		const blob = new Blob([csvData], { type: 'text/csv' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `text_logs_${moment().format('YYYY-MM-DD')}.csv`;
		a.click();
		URL.revokeObjectURL(url);
	};

	const handleVoiceMailLogsDownload = (
		voicemailLogs: any[],
		timeZone: string,
		dateRange?: [any, any] | null,
	) => {
		let filteredLogs = voicemailLogs;

		if (dateRange?.[0] && dateRange?.[1]) {
			const startDate = dateRange[0].valueOf();
			const endDate = dateRange[1].valueOf();
			filteredLogs = voicemailLogs.filter((log) => {
				const logDate = new Date(log.date).valueOf();
				return logDate >= startDate && logDate <= endDate;
			});
		}

		const headingRow = 'Date,From,User name,Transcript\n';
		const csv = filteredLogs
			.map((log) => {
				return `${renderPSTDate(log.date, timeZone)},${log.from},${log.forwardedFrom},${log.voicemail_transcript}`;
			})
			.join('\n');
		const csvData = headingRow + csv;
		const blob = new Blob([csvData], { type: 'text/csv' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `voice_mail_logs_${moment().format('YYYY-MM-DD')}.csv`;
		a.click();
		URL.revokeObjectURL(url);
	};

	const handleDownload = (type: DownloadType, params: DownloadParams) => {
		switch (type) {
			case DownloadType.CALLS:
				downloadCallLogsMutation(params);
				break;
			case DownloadType.TEXTS:
				if (params.textLogs && params.timeZone) {
					handleTextLogsDownload(params.textLogs, params.timeZone, [
						params.startDate,
						params.endDate,
					]);
				}
				break;
			case DownloadType.VOICEMAILS:
				if (params.voicemailLogs && params.timeZone) {
					handleVoiceMailLogsDownload(params.voicemailLogs, params.timeZone, [
						params.startDate,
						params.endDate,
					]);
				}
				break;
		}
	};

	return {
		handleDownload,
		downloadCallLogsStatus,
	};
};
